/* storybook-check-ignore */
import { useState } from 'react';

import { CONSUMER_URL } from 'components/globals';
import { useTrackProperty } from 'components/marketplace/helpers';
import {
  determinePropertyActionability,
  TOUR_ACTION_TEXT,
} from 'components/marketplace/helpers/propertyActionability';
import { PropertyDetailsType } from 'components/marketplace/property';

import { useObservability } from 'helpers/observability';

interface ActionConfig {
  text: string;
  onClick: () => void;
  href?: string;
  target?: string;
  variant: 'primary' | 'secondary' | 'secondaryInverse';
  disabled?: boolean;
  analyticsName: string;
}

interface UseTouringOptions {
  placement: 'footer' | 'lightbox';
}

export const useTouring = (property: PropertyDetailsType, options: UseTouringOptions) => {
  const { placement } = options;
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { isActionable, tourKind, showBuyDirect, disableBuyDirect } =
    determinePropertyActionability(property);

  if (!isActionable || !tourKind) {
    return null;
  }

  const tourUrl = `${CONSUMER_URL}/dashboard/buy/tour?id=${property.addressToken}`;
  const buyDirectUrl = `${CONSUMER_URL}/dashboard/buy/checkout?id=${property.addressToken}`;

  const handleSelfTour = () => {
    trackProperty('property-self-tour', `marketplace-pdp-${placement}-cta`);
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: property.addressToken,
      placement,
    });
  };

  const handleBuyDirect = () => {
    trackProperty('property-buy-direct', `marketplace-pdp-${placement}-cta`);
    trackJourneyEvent('marketplaceProperty', 'offerStart', {
      addressUuid: property.addressToken,
      source: placement,
    });
  };

  const handleTourWithAgent = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => setIsDialogOpen(false);

  // Determine primary and secondary actions based on property state
  let primaryAction: ActionConfig;
  let secondaryAction: ActionConfig | null = null;

  if (showBuyDirect) {
    // BuyDirect eligible properties show Buy button as primary
    primaryAction = {
      text: disableBuyDirect ? 'Offer pending' : 'Buy this home',
      onClick: handleBuyDirect,
      href: buyDirectUrl,
      target: '_blank',
      variant: 'primary',
      disabled: disableBuyDirect,
      analyticsName: `cosmos-marketplace-pdp-${placement}-buy-direct-button`,
    };

    // Show self-tour as secondary action if available
    if (property.canSelfTour) {
      secondaryAction = {
        text: 'Self-tour',
        onClick: handleSelfTour,
        href: tourUrl,
        target: '_blank',
        variant: placement === 'lightbox' ? 'secondaryInverse' : 'secondary',
        analyticsName: `cosmos-marketplace-pdp-${placement}-self-tour-button`,
      };
    }
  } else if (tourKind === 'selfTour') {
    primaryAction = {
      text: TOUR_ACTION_TEXT.selfTour,
      onClick: handleSelfTour,
      href: tourUrl,
      target: '_blank',
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'primary',
      analyticsName: `cosmos-marketplace-pdp-${placement}-self-tour-button`,
    };
  } else {
    primaryAction = {
      text: TOUR_ACTION_TEXT.tourWithAgent,
      onClick: handleTourWithAgent,
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'secondary',
      analyticsName: `cosmos-marketplace-pdp-${placement}-tour-with-agent-button`,
    };
  }

  return {
    primaryAction,
    secondaryAction,
    tourKind,
    isDialogOpen,
    closeDialog,
  };
};
